/**
 * EmailTemplatesPopover.jsx — Wave 1u3 Phase C / F2 (mail templates)
 *
 * Compact popover that lists `/api/user/email-templates`. Lets the user:
 *   - Insert a template into the current compose (replaces subject + body)
 *   - Save the current compose as a new template
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - onInsert: ({ subject, body_html }) => void  — called when user picks a template
 *   - currentSubject: string  — used as default name when "Save as template"
 *   - currentBodyHtml: string — current body to save
 */
import React, { useState, useEffect } from 'react';
import { X, Plus, Loader2, FileText, Trash2 } from 'lucide-react';
import api from '../../api/client';
import { toast } from '../../hooks/use-toast';

export default function EmailTemplatesPopover({
  open,
  onClose,
  onInsert,
  currentSubject = '',
  currentBodyHtml = '',
}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setLoading(true);
    api.get('/user/email-templates')
      .then(({ data }) => { if (alive) setTemplates(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setTemplates([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open]);

  const insert = (tpl) => {
    onInsert?.({ subject: tpl.subject || '', body_html: tpl.body_html || '' });
    onClose?.();
  };

  const saveAsNew = async () => {
    const name = (newName || currentSubject || 'Modèle sans nom').trim();
    if (!name) {
      toast({ title: 'Nom requis', description: 'Donnez un nom à ce modèle.' });
      return;
    }
    if (!currentSubject.trim() && !currentBodyHtml.trim()) {
      toast({ title: 'Composez d\'abord un message', description: 'Le sujet et le corps doivent être renseignés.' });
      return;
    }
    setCreating(true);
    try {
      const { data } = await api.post('/user/email-templates', {
        name, subject: currentSubject || name, body_html: currentBodyHtml, description: '',
      });
      setTemplates((prev) => [data, ...prev]);
      setNewName('');
      toast({ title: 'Modèle enregistré', description: name });
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Création échouée' });
    } finally {
      setCreating(false);
    }
  };

  const remove = async (tpl) => {
    if (!window.confirm(`Supprimer le modèle « ${tpl.name} » ?`)) return;
    try {
      await api.delete(`/user/email-templates/${tpl.id}`);
      setTemplates((prev) => prev.filter((t) => t.id !== tpl.id));
    } catch (err) {
      toast({ title: 'Erreur', description: err?.response?.data?.detail || 'Suppression échouée' });
    }
  };

  if (!open) return null;

  return (
    <div
      // pointer-events-auto: the parent EmailComposeDialog uses pointer-events-none on
      // its outer container, which would otherwise propagate here and block both the
      // backdrop click-away AND every button inside the popover (fix: popover wouldn't close).
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/40 pointer-events-auto"
      onClick={onClose}
      data-testid="email-templates-popover"
    >
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col pointer-events-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b border-[#EFF0F6] flex items-center justify-between">
          <h3 className="text-[14px] font-bold text-[#1A1B2E] flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#4F46E5]" /> Modèles de mails
          </h3>
          <button onClick={onClose} className="text-[#6B6C85] hover:text-[#1A1B2E]" data-testid="email-templates-close">
            <X className="w-4 h-4" />
          </button>
        </header>

        <div className="flex-1 overflow-y-auto p-3 space-y-1">
          {loading ? (
            <div className="text-center py-6 text-[#8E8FA5] text-[12.5px]">
              <Loader2 className="w-4 h-4 animate-spin inline-block mr-1" /> Chargement…
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-8 text-[12.5px] text-[#8E8FA5]">
              Aucun modèle pour l'instant. Enregistrez votre première rédaction ci-dessous.
            </div>
          ) : (
            templates.map((tpl) => (
              <div
                key={tpl.id}
                className="flex items-start gap-2 p-2.5 rounded-lg border border-[#EFF0F6] hover:border-[#C7D2FE] hover:bg-[#FAFAFE] group"
                data-testid={`email-template-${tpl.id}`}
              >
                <button
                  type="button"
                  onClick={() => insert(tpl)}
                  className="flex-1 text-left min-w-0"
                  data-testid={`email-template-insert-${tpl.id}`}
                >
                  <div className="text-[12.5px] font-semibold text-[#1A1B2E] truncate">{tpl.name}</div>
                  <div className="text-[11px] text-[#6B6C85] truncate">{tpl.subject}</div>
                  {tpl.description && (
                    <div className="text-[10.5px] text-[#8E8FA5] mt-0.5 truncate">{tpl.description}</div>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => remove(tpl)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 rounded text-[#B91C1C] hover:bg-[#FEF2F2] flex items-center justify-center flex-shrink-0"
                  title="Supprimer ce modèle"
                  data-testid={`email-template-delete-${tpl.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))
          )}
        </div>

        <footer className="px-4 py-3 border-t border-[#EFF0F6] bg-[#FAFAFE]">
          <div className="text-[11px] font-semibold text-[#6B6C85] mb-1.5">Enregistrer le message actuel comme modèle</div>
          <div className="flex items-center gap-2">
            <input
              data-testid="email-template-new-name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={currentSubject || 'Nom du modèle'}
              className="flex-1 h-8 px-2 text-[12.5px] bg-white border border-[#EFF0F6] rounded focus:outline-none focus:border-[#4F46E5]"
            />
            <button
              type="button"
              onClick={saveAsNew}
              disabled={creating}
              data-testid="email-template-create-btn"
              className="h-8 px-3 rounded bg-[#4F46E5] text-white text-[11.5px] font-semibold inline-flex items-center gap-1 disabled:opacity-50"
            >
              {creating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
              Enregistrer
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}
